import {Action, ChecklistObject, CurrencyType, DateRangeType, FileUploadOptions, KeyTextString, Preset} from "./types";
import {FormGenFreetextType} from "./FormGenAutocomplete";
import {ReactElement} from "react";
import {PathProps} from "./index";
import {FormGenRichContentEditorBase} from "./FormGenRichContentEditorBase";
import {PasskeyConfig} from "./passkey";
import {FormGenContainerMode} from "./FormGenContainer";
import {ImageEditor, ImageOptions} from "./StyledCamera";

export const FORMGEN_STYLE_FLAG_NO_CONTAINER = 0x1 << 1;

export type FormGenSetValueResult = {
  errorMessage?: string,
}

export enum FormGenMetadataType {
  NONE = "none",
  BOOLEAN = "boolean",
  STRING = "string",
  TEXT = "text",
  RICH_TEXT = "rich_text",
  RICH_CONTENT = "rich_content",
  CURRENCY = "currency",
  COPYABLE = "copyable",
  EMAIL = "email",
  NUMBER = "number",
  ENUM = "enum",
  DATE = "date",
  TIME = "time",
  DATETIME = "datetime",
  DATENUM = "datenum",
  TIMENUM = "timenum",
  DATETIMENUM = "datetimenum",
  DATERANGE = "daterange",
  OBJECT = "object",
  CUSTOM = "custom",
  PROFILE_PHOTO = "profile_photo",
  ICON = "icon",
  COLOR = "color",
  AUTOCOMPLETE = "autocomplete",
  COUNTRY_SELECT = "country_select",
  PHONE_NUMBER = "phone_number",
  FILE = "file",
  ARRAY_STRING = "array_string",
  ARRAY_AUTOCOMPLETE = "array_autocomplete",
  ARRAY_FILE = "array_file",
  ARRAY_OBJECT = "array_object",
  PASSKEY = "passkey",
  CHECKLIST = "checklist",
  LATLNG = "latlng",
  BUTTON = "button",
}

export interface FormGenMetadata {
  id?: string,
  index?: number,
  // "none" type is not rendered, but its metadata can be used to hideOn or tab-group other items.
  // Note that "object" type is for nested objects, "custom" is when you want to supply your own renderer.
  // "currency" type is an integer as "basis points", i.e. float number upto 2 decimal places multiplied by 100. Rendered by dividing value by 100.
  type: FormGenMetadataType | string;
  required?: boolean,
  hidden?: boolean,
  autoFocus?: boolean,
  styleFlags?: number,
  inputMode?: "numeric" | "text" | "none" | "url" | "email",
  maxLength?: number,
  multilineStringRows?: number,
  size?: "xsmall" | "small" | "medium" | "large";
  ref?: string;
  name: string;
  readonly?: boolean;
  readonlyFn?: (target: any) => boolean;
  noneditable?: boolean;
  separator?: "above" | "below";
  sectionTitle?: string;
  description?: string;
  placeholder?: string;
  imageEditor?: ImageEditor,
  imageOptions?: ImageOptions,
  // visibility?: string; // Is this used?
  tooltip?: string;
  hideOn?: {
    propertyId: string, // id of property on which to filter.
    hide: (target: any, value: any) => boolean,
  },
  multiStep?: boolean,

  // Number only
  numberMin?: number,
  numberMax?: number,
  numberStep?: number,
  numberIntegerOnly?: boolean,
  numberVariant?: "spinner" | "slider",

  // String only
  slug?: boolean,
  slugLabel?: string,

  // String'ish only
  transformText?: ((str: string) => string) | "uppercase" | "lowercase",

  // Text'ish only
  textAutoEnhancePrompt?: string,

  // Color only
  colorValues?: string[],

  // Currency only
  currencyType?: CurrencyType,

  // Daterange only
  dateRangeDefaultType?: DateRangeType; // Used only when the daterange value is not set.

  // File only
  fileMimeTypes?: string[],
  fileRecord?: boolean, // Allows inline recording if supported by the mimetype (e.g. images, audio & video)
  fileUploadOptions?: FileUploadOptions,
  fileStoredDataSource?: string,
  filePreviewSize?: "small" | "medium" | "large";
  filePreviewType?: "image" | "audio" | "video";
  filePreviewAudioElement?: HTMLAudioElement,
  fileActions?: (args: any[]) => Action[],

  // Enum only
  enumDefaultKey?: string,
  enumDefaultKeyFirst?: boolean,
  enumVariant?: "select" | "button" | "radio",
  enumFilter?: (value: KeyTextString) => boolean,
  enumValues?: KeyTextString[],
  enumDescriptions?: KeyTextString[],
  enumMulti?: boolean,
  enumMultiSortByValues?: boolean, // Sort by enumValues order. Only used when enumMulti is true
  enumPresetsFilter?: (value: Preset) => boolean,
  enumPresetsKeyType?: string, // Name of the presets list (see PresetsKey)

  // Autocomplete only
  autocompleteShowDescriptionInInput?: boolean, // Show description as part of the items in the drop down.
  autocompleteShowDescriptionInMenu?: boolean, // Show description as part of the items in the drop down.
  autocompleteResetOnChange?: boolean,
  autocompleteCreateAction?: Action,
  autocompleteCreateActionInline?: boolean, // Shows create action as part of list of options (e.g Add '<input-text>')
  autocompleteMulti?: boolean,
  autocompleteFreetextType?: FormGenFreetextType,
  autocompleteLoader?: (value?: string, inputText?: string) => Promise<KeyTextString[]>,

  // Rich content only
  richContentType?: typeof FormGenRichContentEditorBase,

  // Custom only
  customRenderer?: (target: any, value: any, setValue: (value: any) => void, args?: {
    path?: PathProps,
    mode: FormGenContainerMode
  }) => ReactElement,

  // Multistep only
  multiStepIndex?: number,

  // Passkey only
  passkeyConfig?: PasskeyConfig,

  // Profile Photo only
  profilePhotoStoredDataSource?: string,
  profilePhotoDefaultImageUrl?: string,
  profilePhotoObjectFit?: "cover" | "contain",

  // Button only
  buttonText?: string,
  buttonDescription?: string,
  buttonOnClick?: (target: any) => void,

  // Country select only
  countrySelectAllowedCountryCodes?: string[];

  // Phone number only
  phoneNumberAllowedCountryCodes?: string[];

  // Array Object only
  arrayObjectCreator?: () => any,

  // Checklist only
  checklistMulti?: boolean,
  checklistCreate?: boolean,
  checklistEdit?: boolean,
  checklistName?: string,
  checklistCreator?: (name?: string) => ChecklistObject,
  checklistLoader?: (value?: string[], name?: string) => Promise<ChecklistObject>,

  onSetValue?: (target: any, value: any) => FormGenSetValueResult | null | undefined | any, // Before value is set.
  onSetTransformValue?: (target: any, value: any) => any,
  onValueSet?: (target: any, value: any, updateCallback: () => Promise<void>) => void, // After value is set.

  _asyncInit?: (metadata: FormGenMetadata, target: any) => Promise<void>,
  _propertyKey?: string,
}

export const formGenMetadataKey = (propertyKey: string) => "__formgen_metadata_" + propertyKey;

interface FormGenFilesProvider {
  get(targetInstance: any): string[] | undefined;
}

class FormGenFilesProviderString implements FormGenFilesProvider {

  constructor(readonly propertyKey: string) {
  }

  get(targetInstance: any): string[] | undefined {
    const value = targetInstance[this.propertyKey];
    return value ? [value as string] : undefined;
  }
}

class FormGenFilesProviderStringArray implements FormGenFilesProvider {

  constructor(readonly propertyKey: string) {
  }

  get(targetInstance: any): string[] | undefined {
    const value = targetInstance[this.propertyKey];
    return value ? value as string[] : undefined;
  }
}

export class FormGenFileStore {

  private readonly _providers: FormGenFilesProvider[] = [];

  addProvider(provider: FormGenFilesProvider): void {
    this._providers.push(provider);
  }

  getFiles(targetInstance: any): string[] {
    const files = new Set<string>();
    for (const provider of this._providers) {
      const itemFiles = provider.get(targetInstance);
      if (itemFiles?.length > 0) {
        itemFiles.forEach(file => files.add(file));
      }
    }
    const childObjectKeys = formGenChildObjectKeysGet(Object.getPrototypeOf(targetInstance));
    for (const childObjectKey of childObjectKeys) {
      const obj = targetInstance[childObjectKey];
      if (Array.isArray(obj)) {
        const array: any[] = obj;
        for (const obj of array) {
          formGenFileStoreGet(Object.getPrototypeOf(obj), true)?.getFiles(obj).forEach(file => files.add(file));
        }
      } else {
        // TODO: See why if nocreate set to false causes a runtime error with emotion/serialize.
        formGenFileStoreGet(Object.getPrototypeOf(obj), true)?.getFiles(obj).forEach(file => {
          files.add(file);
        });
      }
    }
    return [...files.values()];
  }
}

export function formGenFileStoreGet(target: any, nocreate?: boolean): FormGenFileStore {
  let store: FormGenFileStore = target["__formgen_filestore"];
  if (!store && !nocreate) {
    store = new FormGenFileStore();
    target["__formgen_filestore"] = store;
  }
  return store;
}

function formGenFileStoreAdd(target: any, provider: FormGenFilesProvider): void {
  const store = formGenFileStoreGet(target);
  store.addProvider(provider);
}

function formGenChildObjectKeysGet(target: any): string[] {
  let keys: string[] = target["__formgen_childobjectkeys"];
  if (!keys) {
    keys = [];
    target["__formgen_childobjectkeys"] = keys;
  }
  return keys;
}

function formGenChildObjectKeysAdd(target: any, propertyKey: string): void {
  const keys = formGenChildObjectKeysGet(target);
  keys.push(propertyKey);
}

export function FormGen(metadata: FormGenMetadata) {
  // "target" here is the __prototype__ of the object's class, NOT the instance.
  return function (target: any, propertyKey: string) {
    metadata._propertyKey = propertyKey;
    target[formGenMetadataKey(propertyKey)] = metadata;
    if (metadata.enumValues) {
      target[propertyKey + "__formgen_enumvalues"] = metadata.enumValues;
    }
    switch (metadata.type) {
      case FormGenMetadataType.OBJECT:
      case FormGenMetadataType.ARRAY_OBJECT:
        formGenChildObjectKeysAdd(target, propertyKey);
        break;
      case FormGenMetadataType.PROFILE_PHOTO:
      case FormGenMetadataType.FILE:
        formGenFileStoreAdd(target, new FormGenFilesProviderString(propertyKey));
        break;
      case FormGenMetadataType.ARRAY_FILE:
        formGenFileStoreAdd(target, new FormGenFilesProviderStringArray(propertyKey));
        break;
    }
  };
}

export const enumValue = <T>(obj: T, propName: keyof T) => {
  const array = obj[propName.toString() + "__formgen_enumvalues"];
  const key = obj[propName];
  return array.find(item => item.key === key)?.text;
}

export interface FormGenAgent {
  chatResponse(message: string): Promise<string>;
}

export function DatabaseList(metadata: {
  name: string,
}) {
  return function (target: any) {
    console.log("name: " + metadata.name);
  }
}